import { useEffect, useState } from 'react';
import { supabase, checkAuth } from '../lib/supabase';
import { format, differenceInWeeks, startOfDay, addDays, subDays, isToday, addWeeks, subWeeks, differenceInDays } from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import SmartGoalProgress from './SmartGoalProgress';
import WeeklyTasksManager from './WeeklyTasksManager';
import DayCounter from './DayCounter';

interface Sprint {
  id: string;
  start_date: string;
  end_date: string;
  status: string;
}

interface SmartGoal {
  id: string;
  description: string;
  measurement_criteria: string;
  target_value: number;
  current_value: number;
}

interface WeeklyPlan {
  id: string;
  main_priority: string;
  additional_tasks: string[];
  week_number: number;
}

interface DailyGoal {
  id: string;
  primary_task: string;
  secondary_tasks: string[];
  additional_tasks: string[];
  is_completed: boolean;
  completed_secondary_tasks: string[];
  completed_additional_tasks: string[];
  reflection: string | null;
}

function Dashboard() {
  const [currentSprint, setCurrentSprint] = useState<Sprint | null>(null);
  const [goals, setGoals] = useState<SmartGoal[]>([]);
  const [weeklyPlan, setWeeklyPlan] = useState<WeeklyPlan | null>(null);
  const [dailyGoal, setDailyGoal] = useState<DailyGoal | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [taskUpdating, setTaskUpdating] = useState<string | null>(null);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const isAuthed = await checkAuth();
        setIsAuthenticated(isAuthed);
        if (!isAuthed) {
          setLoading(false);
        }
      } catch (err) {
        console.error('Auth check error:', err);
        setIsAuthenticated(false);
        setLoading(false);
      }
    };
    initAuth();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCurrentSprint();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (currentSprint) {
      fetchCurrentWeeklyPlan(currentSprint);
    }
  }, [currentSprint, selectedDate]);

  const fetchCurrentSprint = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setError('Please sign in to continue');
        setLoading(false);
        return;
      }

      const { data: sprint, error: sprintError } = await supabase
        .from('sprints')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'in_progress')
        .maybeSingle();

      if (sprintError) {
        console.error('Error fetching sprint:', sprintError);
        setError('Failed to fetch sprint data');
        return;
      }

      if (sprint) {
        setCurrentSprint(sprint);
        await Promise.all([
          fetchGoals(sprint.id),
          fetchCurrentWeeklyPlan(sprint)
        ]);
      } else {
        setCurrentSprint(null);
      }
    } catch (error) {
      console.error('Error:', error);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchGoals = async (sprintId: string) => {
    try {
      const { data, error } = await supabase
        .from('smart_goals')
        .select('*')
        .eq('sprint_id', sprintId);

      if (error) {
        console.error('Error fetching goals:', error);
        return;
      }

      setGoals(data || []);
    } catch (error) {
      console.error('Error fetching goals:', error);
    }
  };

  const fetchCurrentWeeklyPlan = async (sprint: Sprint) => {
    try {
      const startDate = startOfDay(new Date(sprint.start_date));
      const currentWeek = Math.max(1, differenceInWeeks(selectedDate, startDate) + 1);

      const { data, error } = await supabase
        .from('weekly_plans')
        .select('*')
        .eq('sprint_id', sprint.id)
        .eq('week_number', currentWeek)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching weekly plan:', error);
        return;
      }

      setWeeklyPlan(data || null);
      if (data) {
        await fetchDailyGoal(data.id);
      } else {
        setDailyGoal(null);
      }
    } catch (error) {
      console.error('Error fetching weekly plan:', error);
    }
  };

  const fetchDailyGoal = async (weeklyPlanId: string) => {
    try {
      const date = format(selectedDate, 'yyyy-MM-dd');
      
      const { data, error } = await supabase
        .from('daily_goals')
        .select('*')
        .eq('weekly_plan_id', weeklyPlanId)
        .eq('date', date)
        .maybeSingle();

      if (error) {
        console.error('Error fetching daily goal:', error);
        return;
      }

      setDailyGoal(data);
    } catch (error) {
      console.error('Error fetching daily goal:', error);
    }
  };

  const handlePreviousDay = () => {
    setSelectedDate(prev => subDays(prev, 1));
  };

  const handleNextDay = () => {
    setSelectedDate(prev => addDays(prev, 1));
  };

  const handleTodayClick = () => {
    setSelectedDate(new Date());
  };

  const handlePreviousWeek = () => {
    setSelectedDate(prev => subWeeks(prev, 1));
  };

  const handleNextWeek = () => {
    setSelectedDate(prev => addWeeks(prev, 1));
  };

  const getCurrentWeekNumber = () => {
    if (!currentSprint) return 1;
    const startDate = startOfDay(new Date(currentSprint.start_date));
    return Math.max(1, differenceInWeeks(selectedDate, startDate) + 1);
  };

  const getCurrentDay = () => {
    if (!currentSprint) return 1;
    const startDate = startOfDay(new Date(currentSprint.start_date));
    const today = startOfDay(new Date());
    return Math.min(90, Math.max(1, differenceInDays(today, startDate) + 1));
  };

  const handleDailyGoalCompletion = async () => {
    if (!dailyGoal) return;
    
    try {
      setTaskUpdating('primary');
      setError(null);

      const { error: updateError } = await supabase
        .from('daily_goals')
        .update({ is_completed: !dailyGoal.is_completed })
        .eq('id', dailyGoal.id);

      if (updateError) throw updateError;

      setDailyGoal({
        ...dailyGoal,
        is_completed: !dailyGoal.is_completed
      });
    } catch (err: any) {
      console.error('Error updating daily goal:', err);
      setError('Failed to update daily goal status');
    } finally {
      setTaskUpdating(null);
    }
  };

  const handleSecondaryTaskCompletion = async (task: string) => {
    if (!dailyGoal) return;
    
    try {
      setTaskUpdating(`secondary-${task}`);
      setError(null);

      const completedTasks = dailyGoal.completed_secondary_tasks || [];
      const newCompletedTasks = completedTasks.includes(task)
        ? completedTasks.filter(t => t !== task)
        : [...completedTasks, task];

      const { error: updateError } = await supabase
        .from('daily_goals')
        .update({ completed_secondary_tasks: newCompletedTasks })
        .eq('id', dailyGoal.id);

      if (updateError) throw updateError;

      setDailyGoal({
        ...dailyGoal,
        completed_secondary_tasks: newCompletedTasks
      });
    } catch (err: any) {
      console.error('Error updating secondary task:', err);
      setError('Failed to update task status');
    } finally {
      setTaskUpdating(null);
    }
  };

  const handleAdditionalTaskCompletion = async (task: string) => {
    if (!dailyGoal) return;
    
    try {
      setTaskUpdating(`additional-${task}`);
      setError(null);

      const completedTasks = dailyGoal.completed_additional_tasks || [];
      const newCompletedTasks = completedTasks.includes(task)
        ? completedTasks.filter(t => t !== task)
        : [...completedTasks, task];

      const { error: updateError } = await supabase
        .from('daily_goals')
        .update({ completed_additional_tasks: newCompletedTasks })
        .eq('id', dailyGoal.id);

      if (updateError) throw updateError;

      setDailyGoal({
        ...dailyGoal,
        completed_additional_tasks: newCompletedTasks
      });
    } catch (err: any) {
      console.error('Error updating additional task:', err);
      setError('Failed to update task status');
    } finally {
      setTaskUpdating(null);
    }
  };

  const TaskItem = ({ 
    task, 
    isCompleted, 
    onComplete, 
    type 
  }: { 
    task: string; 
    isCompleted: boolean; 
    onComplete: () => void; 
    type: 'primary' | 'secondary' | 'additional';
  }) => {
    const isUpdating = taskUpdating === (type === 'primary' ? 'primary' : `${type}-${task}`);
    
    return (
      <div 
        className={`relative overflow-hidden transition-all duration-300 ${
          isCompleted ? 'bg-green-50' : 'bg-gray-50'
        } rounded-lg border ${
          isCompleted ? 'border-green-100' : 'border-gray-100'
        } hover:shadow-sm`}
      >
        <div className="flex items-center justify-between p-4">
          <span className={`text-gray-900 ${isCompleted ? 'line-through text-gray-500' : ''}`}>
            {task}
          </span>
          <button
            onClick={onComplete}
            disabled={isUpdating}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all duration-300 ${
              isCompleted
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'hover:bg-gray-100'
            }`}
          >
            {isUpdating ? (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-gray-600" />
            ) : (
              <>
                <CheckCircleIcon
                  className={`h-5 w-5 transition-colors duration-300 ${
                    isCompleted ? 'text-green-500' : 'text-gray-300'
                  }`}
                />
                <span className="text-sm font-medium">
                  {isCompleted ? 'Completed' : 'Mark Complete'}
                </span>
              </>
            )}
          </button>
        </div>
        {isCompleted && (
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500" />
        )}
      </div>
    );
  };

  if (isAuthenticated === null || loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-gray-800"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600 mb-6">Please sign in to continue</p>
          <a
            href="/"
            className="text-sm text-gray-900 hover:text-gray-600 transition-colors"
          >
            Return to Sign In
          </a>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => {
              setError(null);
              fetchCurrentSprint();
            }}
            className="text-sm text-gray-900 hover:text-gray-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!currentSprint) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-medium text-gray-900 mb-4">Welcome to 90-Day Tracker</h1>
          <p className="text-gray-500 mb-8">Start your journey by creating your first sprint</p>
          <a
            href="/create-sprint"
            className="inline-flex items-center px-6 py-3 text-sm text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create New Sprint
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="mb-12">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <div className="text-center lg:text-left">
            <h1 className="text-3xl font-bold text-gray-900 mb-3">90-Day Sprint</h1>
            <p className="text-base text-gray-600 font-medium">
              {format(new Date(currentSprint.start_date), 'MMM d, yyyy')} - {format(new Date(currentSprint.end_date), 'MMM d, yyyy')}
            </p>
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-100">
              <p className="text-lg text-gray-700 italic">
                "Progress is not achieved by luck or accident, but by working on yourself daily."
              </p>
              <p className="text-sm text-gray-500 mt-2">
                - James Clear
              </p>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <DayCounter 
              startDate={currentSprint.start_date} 
              endDate={currentSprint.end_date} 
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
        <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center justify-between">
            <span>Smart Goals</span>
            {goals.length === 0 && (
              <a
                href="/create-sprint"
                className="text-sm text-blue-600 hover:text-blue-700 font-semibold"
              >
                Add Goals
              </a>
            )}
          </h2>
          <div className="space-y-8">
            {goals.map((goal) => (
              <SmartGoalProgress
                key={goal.id}
                goal={goal}
                onUpdate={() => fetchGoals(currentSprint.id)}
              />
            ))}
            {goals.length === 0 && (
              <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-100">
                <p className="text-gray-600 mb-4">No goals set yet</p>
                <p className="text-sm text-gray-500">Add some goals to track your progress</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Week {getCurrentWeekNumber()}</h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={handlePreviousWeek}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
                aria-label="Previous week"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              <button
                onClick={handleNextWeek}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
                aria-label="Next week"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
          {weeklyPlan ? (
            <WeeklyTasksManager
              weeklyPlan={weeklyPlan}
              onUpdate={() => fetchCurrentWeeklyPlan(currentSprint)}
            />
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-100">
              <p className="text-gray-600 mb-6">No weekly plan set for this week</p>
              <a
                href="/weekly-planning"
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Create Weekly Plan
              </a>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl font-bold text-gray-900">Daily Goals</h2>
          <div className="flex items-center space-x-4">
            <button
              onClick={handlePreviousDay}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
              aria-label="Previous day"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <button
              onClick={handleTodayClick}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                isToday(selectedDate)
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {isToday(selectedDate) ? 'Today' : format(selectedDate, 'MMM d')}
            </button>
            <button
              onClick={handleNextDay}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
              aria-label="Next day"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {dailyGoal ? (
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-gray-600 mb-3">Primary Task</h3>
              <TaskItem
                task={dailyGoal.primary_task}
                isCompleted={dailyGoal.is_completed}
                onComplete={handleDailyGoalCompletion}
                type="primary"
              />
            </div>
            
            {dailyGoal.secondary_tasks.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-600 mb-3">Secondary Tasks</h3>
                <div className="space-y-2">
                  {dailyGoal.secondary_tasks.map((task, index) => (
                    <TaskItem
                      key={index}
                      task={task}
                      isCompleted={dailyGoal.completed_secondary_tasks?.includes(task)}
                      onComplete={() => handleSecondaryTaskCompletion(task)}
                      type="secondary"
                    />
                  ))}
                </div>
              </div>
            )}
            
            {dailyGoal.additional_tasks.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-600 mb-3">Additional Tasks</h3>
                <div className="space-y-2">
                  {dailyGoal.additional_tasks.map((task, index) => (
                    <TaskItem
                      key={index}
                      task={task}
                      isCompleted={dailyGoal.completed_additional_tasks?.includes(task)}
                      onComplete={() => handleAdditionalTaskCompletion(task)}
                      type="additional"
                    />
                  ))}
                </div>
              </div>
            )}

            {dailyGoal.reflection && (
              <div>
                <h3 className="text-sm font-semibold text-gray-600 mb-2">Reflection</h3>
                <p className="text-gray-900 bg-gray-50 p-4 rounded-lg border border-gray-100 italic">
                  {dailyGoal.reflection}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-100">
            <p className="text-gray-600 mb-6">
              No daily goals set for {isToday(selectedDate) ? 'today' : format(selectedDate, 'MMM d')}
            </p>
            {isToday(selectedDate) && (
              <a
                href="/daily-goals"
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Set Today's Goals
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;